import * as XLSX from "xlsx";
import type { ColumnDef } from "@tanstack/react-table";
import { recipeRequests } from "@/lib/api/requests/recipes";
import type { RecipeSearchParams } from "@/lib/api/requests/recipes";
import type { Recipe } from "@/types/recipe";
import { adminApi } from "@/lib/api/admin-api";
import type { Material } from "@/types/material";

// ---------------------------------------------------------------------------
// Fetching
// ---------------------------------------------------------------------------

const CHUNK_SIZE = 100;
const ID_BATCH = 10;

// Include allergens + scopes so computed columns (targets, allergens) have data.
const EXPORT_INCLUDES: RecipeSearchParams["include"] = ["allergens", "scopes"];

async function fetchAllPages(
  baseParams: Omit<RecipeSearchParams, "page" | "limit">,
): Promise<Recipe[]> {
  const all: Recipe[] = [];
  let page = 1;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const res = await recipeRequests.list({
      ...baseParams,
      include: EXPORT_INCLUDES,
      page,
      limit: CHUNK_SIZE,
    });
    all.push(...res.data);
    if (!res.meta.hasNext) break;
    page++;
  }
  return all;
}

async function fetchAllMaterialPages(): Promise<Material[]> {
  const MAX_PAGES = 50;
  const all: Material[] = [];
  let page = 1;
  while (page <= MAX_PAGES) {
    const res = await adminApi.getMaterials({ page, limit: CHUNK_SIZE });
    all.push(...res.data);
    if (!res.meta.hasNext) break;
    page++;
  }
  if (page > MAX_PAGES) {
    throw new Error(`Material export exceeded ${MAX_PAGES} pages — export aborted to prevent runaway requests.`);
  }
  return all;
}

async function fetchByIds(ids: number[]): Promise<Recipe[]> {
  const all: Recipe[] = [];
  for (let i = 0; i < ids.length; i += ID_BATCH) {
    const batch = ids.slice(i, i + ID_BATCH);
    const results = await Promise.all(
      batch.map((id) =>
        recipeRequests.getById(id, ["allergens", "scopes", "createdBy", "updatedBy"]),
      ),
    );
    all.push(...results);
  }
  return all;
}

// ---------------------------------------------------------------------------
// Sheet builders — driven by ColumnDef meta.label + accessorKey / accessorFn
// ---------------------------------------------------------------------------

type AnyColumnDef = ColumnDef<Recipe, unknown>;
type AnyMaterialColumnDef = ColumnDef<Material, unknown>;

function resolveValue(col: AnyColumnDef, row: Recipe): unknown {
  const fn = (col as { accessorFn?: (row: Recipe, index: number) => unknown })
    .accessorFn;
  if (typeof fn === "function") return fn(row, 0);

  const key = (col as { accessorKey?: keyof Recipe }).accessorKey;
  if (key) return row[key] ?? "";

  return "";
}

function buildMaterialSheet(
  materials: Material[],
  columns: AnyMaterialColumnDef[],
): XLSX.WorkSheet {
  const exportCols = columns.filter((c) => c.meta?.label);
  const headers = exportCols.map((c) => c.meta!.label!);
  const body = materials.map((row) =>
    exportCols.map((col) => {
      const fn = (col as { accessorFn?: (row: Material, index: number) => unknown }).accessorFn;
      if (typeof fn === "function") return fn(row, 0) ?? "";
      const key = (col as { accessorKey?: keyof Material }).accessorKey;
      const val = key ? row[key] : "";
      return val === null || val === undefined ? "" : val;
    }),
  );
  return XLSX.utils.aoa_to_sheet([headers, ...body]);
}

function buildSheet(
  recipes: Recipe[],
  columns: AnyColumnDef[],
): XLSX.WorkSheet {
  // Only export columns that have a label (actions column etc. are excluded)
  const exportCols = columns.filter((c) => c.meta?.label);
  const headers = exportCols.map((c) => c.meta!.label!);
  const body = recipes.map((row) =>
    exportCols.map((col) => {
      const val = resolveValue(col, row);
      return val === null || val === undefined ? "" : val;
    }),
  );
  return XLSX.utils.aoa_to_sheet([headers, ...body]);
}

/**
 * Merges two groups of recipes (with potentially different column sets) into a
 * single flat sheet for CSV export.
 *
 * A "Category" column is prepended so the reader can still distinguish between
 * "Menu Item" and "Prep Recipe" rows. The full union of labeled column headers
 * from both groups is used; cells that have no matching column for a given row
 * are left blank.
 */
function buildMergedSheet(
  menuItems: Recipe[],
  menuItemColumns: AnyColumnDef[],
  prepRecipes: Recipe[],
  prepRecipeColumns: AnyColumnDef[],
): XLSX.WorkSheet {
  const exportMenuCols = menuItemColumns.filter((c) => c.meta?.label);
  const exportPrepCols = prepRecipeColumns.filter((c) => c.meta?.label);

  // Build a de-duplicated, ordered list of all labels.
  const allLabels: string[] = ["Category"];
  const seenLabels = new Set<string>(["Category"]);
  for (const col of [...exportMenuCols, ...exportPrepCols]) {
    const label = col.meta!.label!;
    if (!seenLabels.has(label)) {
      allLabels.push(label);
      seenLabels.add(label);
    }
  }

  function buildRows(recipes: Recipe[], cols: AnyColumnDef[], category: string): unknown[][] {
    return recipes.map((row) => {
      const labelToValue = new Map<string, unknown>();
      for (const col of cols) {
        if (!col.meta?.label) continue;
        const val = resolveValue(col, row);
        labelToValue.set(col.meta.label, val === null || val === undefined ? "" : val);
      }
      return allLabels.map((label) =>
        label === "Category" ? category : (labelToValue.get(label) ?? ""),
      );
    });
  }

  const menuRows = buildRows(menuItems, exportMenuCols, "Menu Item");
  const prepRows = buildRows(prepRecipes, exportPrepCols, "Prep Recipe");

  return XLSX.utils.aoa_to_sheet([allLabels, ...menuRows, ...prepRows]);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ExportRecipesOptions {
  includeMenuItems: boolean;
  menuItemColumns: AnyColumnDef[];

  includePrepRecipes: boolean;
  prepRecipeColumns: AnyColumnDef[];

  includeIngredients: boolean;
  ingredientColumns: AnyMaterialColumnDef[];

  fileType: "xlsx" | "csv";

  /** Recipe IDs to export. When empty, all recipes matching baseParams are fetched. */
  selectedMenuItemIds?: number[];
  selectedPrepRecipeIds?: number[];

  /** Filters forwarded to the list endpoint when fetching all. */
  baseParams?: Omit<RecipeSearchParams, "page" | "limit">;
}

export async function exportRecipesToFile(
  options: ExportRecipesOptions,
): Promise<void> {
  const {
    includeMenuItems,
    menuItemColumns,
    includePrepRecipes,
    prepRecipeColumns,
    includeIngredients,
    ingredientColumns,
    fileType,
    selectedMenuItemIds,
    selectedPrepRecipeIds,
    baseParams = {},
  } = options;

  const wb = XLSX.utils.book_new();

  if (fileType === "csv") {
    // CSV does not support multiple worksheets — merge everything into one sheet.
    const menuItems: Recipe[] =
      includeMenuItems
        ? selectedMenuItemIds && selectedMenuItemIds.length > 0
          ? await fetchByIds(selectedMenuItemIds)
          : await fetchAllPages(baseParams)
        : [];

    const prepRecipes: Recipe[] =
      includePrepRecipes
        ? selectedPrepRecipeIds && selectedPrepRecipeIds.length > 0
          ? await fetchByIds(selectedPrepRecipeIds)
          : await fetchAllPages(baseParams)
        : [];

    XLSX.utils.book_append_sheet(
      wb,
      buildMergedSheet(menuItems, menuItemColumns, prepRecipes, prepRecipeColumns),
      "Recipes",
    );

    // CSV: ingredients go in separate files since CSV is single-sheet
    if (includeIngredients) {
      const materials = await fetchAllMaterialPages();
      const date = new Date().toISOString().slice(0, 10);

      const ingredientWb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        ingredientWb,
        buildMaterialSheet(materials, ingredientColumns),
        "Ingredients",
      );
      XLSX.writeFile(ingredientWb, `ingredients-${date}.csv`);

      // Sub-ingredient relations go in a third file (CSV is single-sheet)
      const subIngRows: unknown[][] = [
        ["Parent Name", "Child Name", "Quantity", "Unit"],
      ];
      for (const mat of materials) {
        for (const si of mat.subIngredients ?? []) {
          const childName = si.childMaterial?.name ?? "";
          if (childName) subIngRows.push([mat.name, childName, si.quantity, si.unit]);
        }
      }
      if (subIngRows.length > 1) {
        const subIngWb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(subIngWb, XLSX.utils.aoa_to_sheet(subIngRows), "Sub-Ingredients");
        XLSX.writeFile(subIngWb, `sub-ingredients-${date}.csv`);
      }
    }
  } else {
    // XLSX supports multiple worksheets — keep each category on its own sheet.
    if (includeMenuItems) {
      const recipes =
        selectedMenuItemIds && selectedMenuItemIds.length > 0
          ? await fetchByIds(selectedMenuItemIds)
          : await fetchAllPages(baseParams);
      XLSX.utils.book_append_sheet(
        wb,
        buildSheet(recipes, menuItemColumns),
        "Menu Items",
      );
    }

    if (includePrepRecipes) {
      const recipes =
        selectedPrepRecipeIds && selectedPrepRecipeIds.length > 0
          ? await fetchByIds(selectedPrepRecipeIds)
          : await fetchAllPages(baseParams);
      XLSX.utils.book_append_sheet(
        wb,
        buildSheet(recipes, prepRecipeColumns),
        "Prep Recipes",
      );
    }

    if (includeIngredients) {
      const materials = await fetchAllMaterialPages();
      XLSX.utils.book_append_sheet(
        wb,
        buildMaterialSheet(materials, ingredientColumns),
        "Ingredients",
      );

      // Sub-Ingredients sheet: one row per sub-ingredient relation.
      const subIngRows: unknown[][] = [
        ["Parent Name", "Child Name", "Quantity", "Unit"],
      ];
      for (const mat of materials) {
        for (const si of mat.subIngredients ?? []) {
          const childName = si.childMaterial?.name ?? "";
          if (childName) {
            subIngRows.push([mat.name, childName, si.quantity, si.unit]);
          }
        }
      }
      if (subIngRows.length > 1) {
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.aoa_to_sheet(subIngRows),
          "Sub-Ingredients",
        );
      }
    }
  }

  // Only write the main workbook if it has at least one sheet with recipe data.
  // For CSV, ingredients-only exports are written to separate files above and
  // wb would contain only an empty merged sheet — skip it in that case.
  if (wb.SheetNames.length > 0 && (fileType !== 'csv' || includeMenuItems || includePrepRecipes)) {
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `recipes-${date}.${fileType}`);
  }
}
